import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Crown, Users, Trophy, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const HostEventManagement = () => {
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [votesReport, setVotesReport] = useState(null);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [selectedParticipantVotes, setSelectedParticipantVotes] = useState(null);
  const [userVotesKakoId, setUserVotesKakoId] = useState('');
  const [userVotesData, setUserVotesData] = useState(null);
  const [userVotesMade, setUserVotesMade] = useState(null);
  const [jackpotData, setJackpotData] = useState(null);
  const [splitDraft, setSplitDraft] = useState({ top1: 50, top2: 30, top3: 20 });
  const [loading, setLoading] = useState(true);
  const [rulesText, setRulesText] = useState('');
  const [prizeText, setPrizeText] = useState('');

  useEffect(() => {
    loadConfig();
    loadParticipants();
    loadVotesReport();
    loadCurrentWinner();
    loadWinnersHistory();
    loadJackpot();
  }, []);

  const loadConfig = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-event/config`);
      setConfig(response.data);
      setRulesText(response.data.rules_text);
      setPrizeText(response.data.prize_description);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-event/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Erro ao carregar participantes:', error);
    }
  };

  const loadVotesReport = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/host-event/votes/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVotesReport(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    }
  };


  const loadCurrentWinner = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-event/winner/current-with-data`);
      setCurrentWinner(response.data);
    } catch (error) {
      console.error('Erro ao carregar ganhador:', error);
    }
  };

  const loadWinnersHistory = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-event/winner/history`);
      setWinnersHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const archiveWinner = async () => {
    if (!window.confirm('Arquivar o ganhador atual no histórico?')) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/host-event/winner/archive`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ganhador arquivado!');
      loadCurrentWinner();
      loadWinnersHistory();
    } catch (error) {
      console.error('Erro ao arquivar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao arquivar');
    }
  };

  const removeWinner = async () => {
    if (!window.confirm('Remover o ganhador atual?')) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/host-event/winner/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ganhador removido');
      loadCurrentWinner();
      loadConfig();
    } catch (error) {
      console.error('Erro ao remover:', error);
      toast.error('Erro ao remover ganhador');
    }
  };

  const deleteHistoryEdition = async (editionId, eventDate) => {
    if (!window.confirm(`Excluir a edição de ${eventDate}?`)) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/host-event/winner/history/${editionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Edição removida');
      loadWinnersHistory();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir edição');
    }
  };



  const saveWinner = async (kakoId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/switches`,
        { 
          show_inscription: config.show_inscription,
          show_voting: config.show_voting,
          show_winner: config.show_winner,
          winner_kako_id: kakoId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ganhador definido!');
      loadConfig();
      loadCurrentWinner();
    } catch (error) {
      console.error('Erro ao salvar ganhador:', error);
      toast.error('Erro ao definir ganhador');
    }
  };


  const toggleSwitch = async (field, value) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/switches`,
        { 
          show_inscription: field === 'show_inscription' ? value : config.show_inscription,
          show_voting: field === 'show_voting' ? value : config.show_voting,
          show_winner: field === 'show_winner' ? value : config.show_winner
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Configuração atualizada!');
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const toggleInscriptions = async (value) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/inscription?open_inscriptions=${value}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Inscrições ${value ? 'abertas' : 'fechadas'}!`);
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const toggleVoting = async (value) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/voting?open_voting=${value}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Votação ${value ? 'iniciada' : 'encerrada'}!`);
      loadConfig();
      loadVotesReport();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const saveRules = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/rules`,
        { rules_text: rulesText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Regras atualizadas!');
      loadConfig();
    } catch (error) {
      toast.error('Erro ao salvar regras');
    }
  };

  const savePrize = null; // Prêmio fixo substituído pelo Jackpot do evento

  const loadJackpot = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-event/jackpot`);
      setJackpotData(response.data);
      setSplitDraft(response.data.split);
    } catch (error) {
      console.error('Erro ao carregar jackpot:', error);
    }
  };

  const saveJackpotSplit = async () => {
    const total = (parseInt(splitDraft.top1) || 0) + (parseInt(splitDraft.top2) || 0) + (parseInt(splitDraft.top3) || 0);
    if (total !== 100) {
      toast.error(`A soma deve ser 100% (atual: ${total}%)`);
      return;
    }
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${BACKEND_URL}/api/host-event/config/jackpot-split`,
        { top1: parseInt(splitDraft.top1), top2: parseInt(splitDraft.top2), top3: parseInt(splitDraft.top3) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Divisão do Jackpot salva!');
      loadJackpot();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar divisão');
    }
  };

  const loadParticipantVotes = async (kakoId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/host-event/votes/participant/${kakoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedParticipantVotes(response.data);
    } catch (error) {
      console.error('Erro ao carregar votos:', error);
      toast.error('Erro ao carregar votos');
    }
  };

  const removeVote = async (voteId, participantKakoId) => {
    if (!window.confirm('Remover este voto? Votos serão devolvidos ao usuário.')) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/host-event/votes/${voteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voto removido!');
      
      // Recarregar TUDO
      await Promise.all([
        loadVotesReport(),
        loadParticipants(),
        participantKakoId ? loadParticipantVotes(participantKakoId) : Promise.resolve()
      ]);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao remover voto');
    }
  };

  const searchUserVotes = async () => {
    if (!userVotesKakoId) {
      toast.error('Digite um KAKO ID');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/host-event/my-votes/${userVotesKakoId}`);
      setUserVotesData(response.data);
      
      // Também buscar votos feitos pelo usuário
      const votesMadeResponse = await axios.get(`${BACKEND_URL}/api/host-event/votes/by-user/${userVotesKakoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserVotesMade(votesMadeResponse.data);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Usuário não encontrado');
    }
  };

  const removeParticipant = async (participantId) => {
    if (!window.confirm('Remover este participante?')) return;
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.delete(`${BACKEND_URL}/api/host-event/participant/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Participante removido!');
      loadParticipants();
      loadVotesReport();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">👑 MEU HOST PREFERIDO</h2>
        <p className="text-gray-600">Gerenciar evento de votação</p>
      </div>

      {/* Switches Globais */}
      <Card>
        <CardHeader>
          <CardTitle>⚙️ Configurações de Visibilidade</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded">
            <Label>Mostrar Aba de Inscrição para o Público:</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={config?.show_inscription || false}
                onCheckedChange={(v) => toggleSwitch('show_inscription', v)}
              />
              <Badge variant={config?.show_inscription ? 'default' : 'secondary'}>
                {config?.show_inscription ? 'VISÍVEL' : 'OCULTO'}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded">
            <Label>Mostrar Aba de Votação para o Público:</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={config?.show_voting || false}
                onCheckedChange={(v) => toggleSwitch('show_voting', v)}
              />
              <Badge variant={config?.show_voting ? 'default' : 'secondary'}>
                {config?.show_voting ? 'VISÍVEL' : 'OCULTO'}
              </Badge>
            </div>
          </div>
          
          {/* Switch Mostrar Aba Ganhador */}
          <div className="flex items-center justify-between p-3 border-2 border-pink-300 rounded bg-pink-50">
            <div>
              <Label className="font-semibold">Mostrar Aba de Ganhador:</Label>
              <p className="text-xs text-gray-600 mt-1">Exibe aba do ganhador para o público</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config?.show_winner || false}
                onCheckedChange={(v) => toggleSwitch('show_winner', v)}
              />
              <Badge variant={config?.show_winner ? 'default' : 'secondary'}>
                {config?.show_winner ? 'VISÍVEL' : 'OCULTO'}
              </Badge>
            </div>
          </div>
          
          {/* Cadastro de votante e senha p/ votar REMOVIDOS:
              votar agora exige login no perfil do cliente */}

        </CardContent>
      </Card>
      

      {/* 🏆 Jackpot do Evento */}
      <Card className="border-2 border-amber-400" data-testid="admin-host-jackpot-card">
        <CardHeader className="bg-amber-50">
          <CardTitle>🏆 Jackpot do Evento (500 moedas por voto recebido)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="p-4 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 border-2 border-amber-300 text-center">
            <p className="text-xs text-amber-800 font-semibold uppercase tracking-wide">Acumulado atual</p>
            <p className="text-4xl font-black text-amber-600" data-testid="admin-jackpot-total">
              {(jackpotData?.jackpot_coins || 0).toLocaleString('pt-BR')} 🪙
            </p>
            <p className="text-xs text-amber-700 mt-1">Pagamento aos TOP 3 é feito MANUALMENTE pelo admin no fim do evento. Arquivar o vencedor zera o Jackpot e guarda os valores no histórico.</p>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {['top1', 'top2', 'top3'].map((key, idx) => (
              <div key={key} className="p-3 border-2 rounded-lg bg-white space-y-2">
                <Label className="font-semibold text-sm">{['🥇 TOP 1', '🥈 TOP 2', '🥉 TOP 3'][idx]}</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={splitDraft[key]}
                    onChange={(e) => setSplitDraft({ ...splitDraft, [key]: e.target.value })}
                    data-testid={`admin-jackpot-split-${key}`}
                    className="h-9"
                  />
                  <span className="text-sm font-bold">%</span>
                </div>
                <p className="text-xs text-green-700 font-semibold">
                  ≈ {Math.floor((jackpotData?.jackpot_coins || 0) * (parseInt(splitDraft[key]) || 0) / 100).toLocaleString('pt-BR')} moedas
                </p>
                {jackpotData?.positions?.[idx]?.nickname && (
                  <p className="text-xs text-gray-600 truncate">{jackpotData.positions[idx].nickname} ({jackpotData.positions[idx].total_votes} votos)</p>
                )}
              </div>
            ))}
          </div>
          
          <Button onClick={saveJackpotSplit} data-testid="admin-jackpot-split-save" className="w-full bg-amber-600 hover:bg-amber-700">
            Salvar Divisão do Jackpot
          </Button>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="inscritos" className="w-full">
        <TabsList>
          <TabsTrigger value="inscritos">
            <Users className="w-4 h-4 mr-2" />
            Inscritos
          </TabsTrigger>
          <TabsTrigger value="votacao">
            <Trophy className="w-4 h-4 mr-2" />
            Votação
          </TabsTrigger>
          <TabsTrigger value="ganhador">
            <Crown className="w-4 h-4 mr-2" />
            Ganhador
          </TabsTrigger>
          <TabsTrigger value="regras">
            <FileText className="w-4 h-4 mr-2" />
            Regras
          </TabsTrigger>
        </TabsList>

        {/* Aba Inscritos */}
        <TabsContent value="inscritos" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Participantes Inscritos</CardTitle>
                <div className="flex items-center gap-3">
                  <Label>Inscrições:</Label>
                  <Switch
                    checked={config?.inscriptions_open || false}
                    onCheckedChange={toggleInscriptions}
                  />
                  <Badge variant={config?.inscriptions_open ? 'default' : 'secondary'}>
                    {config?.inscriptions_open ? 'ABERTAS' : 'FECHADAS'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{participants.length} participantes</p>
              
              {participants.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum participante inscrito</p>
              ) : (
                <div className="space-y-3">
                  {participants.map((p, idx) => (
                    <div key={p.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-lg">{idx + 1}º</span>
                        {p.kako_avatar && (
                          <img src={p.kako_avatar} alt="" className="w-10 h-10 rounded-full" />
                        )}
                        <div>
                          <p className="font-semibold">{p.kako_nickname}</p>
                          <p className="text-xs text-gray-600">ID: {p.kako_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-600">{p.total_votes} votos</Badge>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeParticipant(p.id || p.kako_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Votação */}
        <TabsContent value="votacao" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Controle de Votação</CardTitle>
                <div className="flex items-center gap-3">
                  <Label>Votação:</Label>
                  <Switch
                    checked={config?.voting_open || false}
                    onCheckedChange={toggleVoting}
                  />
                  <Badge variant={config?.voting_open ? 'default' : 'secondary'}>
                    {config?.voting_open ? 'ABERTA' : 'FECHADA'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {votesReport && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-blue-50 rounded">
                      <p className="text-xs text-gray-600">Total de Votos</p>
                      <p className="text-2xl font-bold text-blue-600">{votesReport.total_votes_cast}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded">
                      <p className="text-xs text-gray-600">Total de Votantes</p>
                      <p className="text-2xl font-bold text-green-600">{votesReport.total_voters}</p>
                    </div>
                  </div>

                  {/* Ranking com histórico */}
                  <div className="space-y-3">
                    {votesReport.participants.map((p, idx) => (
                      <div key={p.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`font-bold text-xl ${
                              idx === 0 ? 'text-yellow-500' : 
                              idx === 1 ? 'text-gray-400' : 
                              idx === 2 ? 'text-orange-500' : 'text-gray-600'
                            }`}>
                              {idx === 0 && '🥇'}
                              {idx === 1 && '🥈'}
                              {idx === 2 && '🥉'}
                              {idx > 2 && `${idx + 1}º`}
                            </span>
                            {p.kako_avatar && (
                              <img src={p.kako_avatar} alt="" className="w-12 h-12 rounded-full" />
                            )}
                            <div>
                              <p className="font-bold">{p.kako_nickname}</p>
                              <p className="text-xs text-gray-600">ID: {p.kako_id}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-600">{p.total_votes}</p>
                            <p className="text-xs text-gray-600">votos</p>
                          </div>
                        </div>
                        
                        {/* Histórico de votos */}
                        {p.votes_history && p.votes_history.length > 0 && (
                          <details className="mt-3">
                            <summary className="text-sm font-semibold cursor-pointer text-blue-600">
                              Ver {p.votes_history.length} voto(s) recebido(s)
                            </summary>
                            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                              {p.votes_history.map((vote, i) => (
                                <div key={i} className="text-xs p-2 bg-gray-50 rounded flex justify-between items-center gap-2">
                                  <span className="flex-1">{vote.voter_nickname || vote.voter_kako_id}</span>
                                  <span className="font-bold">+{vote.votes_count} votos</span>
                                  <Button 
                                    size="sm" 
                                    variant="destructive" 
                                    onClick={() => removeVote(vote.id, p.kako_id)}
                                    className="h-6 px-2 text-xs"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Card Gerenciar Votos de Usuário */}
              <Card className="mt-6 border-2 border-orange-300">
                <CardHeader className="bg-orange-50">
                  <CardTitle>⚙️ Gerenciar Votos de Usuário</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="KAKO ID do usuário"
                      value={userVotesKakoId}
                      onChange={(e) => setUserVotesKakoId(e.target.value)}
                    />
                    <Button onClick={searchUserVotes}>Buscar</Button>
                  </div>
                  
                  {userVotesData && (
                    <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                      <p className="font-semibold">Usuário: {userVotesKakoId}</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-white rounded">
                          <p className="flex items-center gap-1 text-xs text-gray-600"><img src="/roleta/azulzinha.png" alt="" className="w-3.5 h-3.5" /> Saldo Azulzinhas</p>
                          <p className="text-2xl font-bold text-blue-600">{(userVotesData.blue_balance || 0).toLocaleString('pt-BR')}</p>
                        </div>
                        <div className="p-3 bg-white rounded">
                          <p className="text-xs text-gray-600">Votos Disponíveis (500 Azulzinhas = 1)</p>
                          <p className="text-2xl font-bold text-green-600">{userVotesData.remaining_votes || 0}</p>
                        </div>
                        <div className="p-3 bg-white rounded">
                          <p className="text-xs text-gray-600">Votos Usados</p>
                          <p className="text-2xl font-bold text-purple-600">{userVotesData.votes_used || 0}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        💡 Para ajustar votos, ajuste o saldo de Azulzinhas do cliente na aba <strong>Azulzinhas</strong>.
                      </p>
                      
                      {/* Votos Feitos pelo Usuário */}
                      {userVotesMade && userVotesMade.length > 0 && (
                        <div className="space-y-2 pt-3 border-t">
                          <Label className="font-semibold">Votos Feitos pelo Usuário ({userVotesMade.length}):</Label>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {userVotesMade.map((vote, idx) => (
                              <div key={idx} className="text-xs p-2 bg-white rounded border flex justify-between items-center gap-2">
                                <span className="flex-1">Votou em: {vote.host_nickname || vote.participant_kako_id}</span>
                                <span className="font-semibold">{vote.votes_count}x</span>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  onClick={() => removeVote(vote.id, vote.participant_kako_id)}
                                  className="h-6 px-2"
                                >
                                  🗑️
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                    </div>
                  )}
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Regras */}
        <TabsContent value="regras" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>📜 Regras do Evento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={rulesText}
                onChange={(e) => setRulesText(e.target.value)}
                rows={15}
                placeholder="Digite as regras do evento..."
              />
              <Button onClick={saveRules} className="w-full bg-blue-600">
                Salvar Regras
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Aba Cadastro */}
        {/* Aba Ganhador */}
        <TabsContent value="ganhador" className="mt-4">
          <Tabs defaultValue="definir" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="definir">✏️ Definir</TabsTrigger>
              <TabsTrigger value="atual">🏆 Atual</TabsTrigger>
              <TabsTrigger value="history">📜 Edições Anteriores</TabsTrigger>
            </TabsList>

            {/* Sub-aba: Definir */}
            <TabsContent value="definir" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>✏️ Definir Ganhador</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>🏆 KAKO ID do Ganhador</Label>
                    <Input
                      value={config?.winner_kako_id || ''}
                      onChange={(e) => setConfig({ ...config, winner_kako_id: e.target.value })}
                      placeholder="ID do host vencedor"
                    />
                  </div>
                  <Button onClick={() => saveWinner(config?.winner_kako_id)} className="w-full">
                    💾 Salvar Ganhador
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sub-aba: Atual */}
            <TabsContent value="atual" className="space-y-4 mt-4">
              {!currentWinner ? (
                <p className="text-gray-500 text-center py-8">Nenhum ganhador definido</p>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Button onClick={archiveWinner} variant="outline" className="flex-1">
                      📦 Arquivar Ganhador
                    </Button>
                    <Button onClick={removeWinner} variant="destructive" className="flex-1">
                      🗑️ Remover Ganhador
                    </Button>
                  </div>

                  <Card className="border-4 border-pink-400 bg-gradient-to-br from-pink-50 to-purple-100">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-3xl font-bold text-pink-700">👑 VENCEDOR</h3>
                      <div className="relative w-[280px] h-[280px] mx-auto flex items-center justify-center">
                        {currentWinner?.avatar && (
                          <img src={currentWinner.avatar} alt="" className="w-40 h-40 rounded-full border-4 border-pink-500" />
                        )}
                        <img src="/frame-host-winner.png" alt="Vencedor" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                      </div>
                      <p className="text-2xl font-bold">{currentWinner?.nickname || 'Desconhecido'}</p>
                      <p className="text-sm text-gray-600">ID: {currentWinner?.kako_id}</p>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Sub-aba: Edições Anteriores */}
            <TabsContent value="history" className="space-y-3 mt-4">
              {winnersHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nenhum evento anterior</p>
              ) : (
                winnersHistory.map((event, idx) => (
                  <Card key={idx} className="border-2">
                    <CardHeader className="bg-purple-50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">🏆 {winnersHistory.length - idx}ª Edição - {event.event_date}</CardTitle>
                        <Button size="sm" variant="destructive" onClick={() => deleteHistoryEdition(event.edition_id, event.event_date)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 bg-pink-50 p-3 rounded-lg">
                        {event.winner_avatar && (
                          <img src={event.winner_avatar} alt="" className="w-16 h-16 rounded-full border-2 border-pink-500" />
                        )}
                        <div className="flex-1">
                          <p className="font-bold">👑 {event.winner_nickname}</p>
                          <p className="text-sm text-pink-700 font-semibold">VENCEDOR</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default HostEventManagement;
